import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useContext } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { ICurrentWorkspaceStore } from '../../../../modules/workspaces/currentWorkspace/CurrentWorkspace.store';
import { doNothing } from '../../../helpers/function.helpers';
import { useLoader } from '../../../helpers/useLoader';
import { ACDLError } from '../../../models/error.model';
import { Home } from '../../../routes/config/Home';
import { ImageUploadForm } from '../components/ImageUploadForm';
import { ICreateDatasetService } from '../createDataset.service';
import { ICreateDatasetFilesService } from '../createDatasetFiles.service';
import { Datasets } from '../datasets.context';

export const CreateDatasetFilesContainer = observer((): ReactElement => {
  const createDatasetService = useInjection(ICreateDatasetService.$);
  const createDatasetFilesService = useInjection(ICreateDatasetFilesService.$);
  const currentWorkspaceStore = useInjection(ICurrentWorkspaceStore.$);

  const datasetsContext = useContext(Datasets);
  const { datasetDraftId } = useParams<{ datasetDraftId: string }>();
  const history = useHistory();
  const { withLoaderAsync } = useLoader();

  const handleCreateDatasetAsync = async (): Promise<void> => {
    await withLoaderAsync(async (): Promise<void> => {
      datasetsContext.service.markStale();
      const result = await createDatasetService.createDatasetFromDraftAsync();
      if (result instanceof ACDLError) {
        return;
      }

      const workspaceId = currentWorkspaceStore.currentWorkspace.id;

      history.push(Home.Datasets.Details.Images.withParams({ workspaceId, datasetId: datasetDraftId }));
      await datasetsContext.service.loadUserDatasetAsync();
    }, 'upload');
  };

  const onCancel = (): void => {
    createDatasetFilesService.cleanImagesFromFile();
  };

  const { draft, isValid } = createDatasetService.datasetsDraftStore;
  if (!draft) {
    return <div />;
  }

  return (
    <ImageUploadForm
      datasetId={draft.id}
      datasetCreatorId={draft.author}
      datasetStatus={'Draft'}
      imagesRejectedFromFile={draft.imagesRejectedFromFile || []}
      imagesToUploadFromFile={draft.imagesToUploadFromFile || []}
      isCreationEnabled={isValid && !draft.willExceedsUploadImageSizeLimit}
      isCancelEnabled={isValid && !draft.willExceedsUploadImageSizeLimit}
      isProcessingImages={createDatasetFilesService.isProcessingImages}
      onFormSubmit={handleCreateDatasetAsync}
      addImagesToDataset={async (files, rejections) => {
        return createDatasetFilesService.addImagesToDraft(files, rejections);
      }}
      cleanImagesFromFile={() => {
        createDatasetFilesService.cleanImagesFromFile();
      }}
      willExceedsUploadImageSizeLimit={draft.willExceedsUploadImageSizeLimit}
      imagePreValidationWasSuccessful={draft.imagePreValidationWasSuccessful}
      ownerPlan={draft.ownerPlan}
      toggleUploadConfirmationModal={doNothing}
      isConfirmationModalOpen={false}
      isEncryptionEnabled={currentWorkspaceStore.currentWorkspace.encryption.encrypted || false}
      onCancel={onCancel}
      loaderKey='upload'
      currentFilesSizeInB={0}
    />
  );
});
